// Paid boosts: sold for diamonds in CONFIG.paidBoosts.tiers lengths (1 h to 30 d). Three kinds:
//   txp   Trainer XP  ×1.5 on the trainer's kill XP
//   cxp   Creature XP ×1.5 on the lead's kill XP (and every shrine and XP Charm share of it)
//   loot  Loot        ×1.5 on kill coins (the old junk loot's value is part of them now: shared/xp.ts
//                     LOOT_SHARE). Never any drop, relic, Aura or catch odds: we never sell odds.
// Buying a kind again adds time; different kinds multiply. A kind does not stack with the free boost of the
// same effect (the XP boost and Coin boost that quests, the pass and the Arena give): the bought time starts
// where the free one ends. Nothing caught or dropped under these is marked (only the Aura Charm marks catches).
import { CONFIG } from './config.ts';
import type { TrainerDTO } from './protocol.ts';

export type PaidBoostKind = 'txp' | 'cxp' | 'loot';
export const PAID_BOOST_KINDS: readonly PaidBoostKind[] = ['txp', 'cxp', 'loot'];

/** The one notation every boost list uses for a multiplier: "×1.5 XP", "×1.1 XP and loot". */
export const multX = (m: number, what = 'XP') => `×${+m.toFixed(2)} ${what}`;
/** A percent bonus (the Guild War's +N%) in the same notation. */
export const pctX = (pct: number, what = 'XP') => multX(1 + pct / 100, what);

const M = CONFIG.paidBoosts.mult;
export const PAID_BOOST: Record<PaidBoostKind, { name: string; short: string; effect: string; desc: string }> = {
  txp: { name: 'Trainer XP', short: 'Trainer XP', effect: multX(M.txp, 'trainer XP'), desc: `${multX(M.txp, 'trainer kill XP')}.` },
  cxp: { name: 'Creature XP', short: 'Creature XP', effect: multX(M.cxp, 'creature XP'), desc: `${multX(M.cxp, 'creature kill XP')}, shrine and XP Charm shares included.` },
  loot: { name: 'Loot', short: 'Loot', effect: multX(M.loot, 'loot'), desc: `${multX(M.loot, 'kill coins')}. Drops are unchanged.` },
};

type Boosts = TrainerDTO['boosts'];

/** Offer id for one kind and length, e.g. 'boost-txp-24'. */
export const boostOfferId = (kind: PaidBoostKind, hours: number) => `boost-${kind}-${hours}`;

/** The kind, length and price an offer id names; null when it is not a paid-boost offer. */
export function parseBoostOffer(id: unknown): { kind: PaidBoostKind; hours: number; price: number } | null {
  if (typeof id !== 'string') return null;
  const m = /^boost-(txp|cxp|loot)-(\d+)$/.exec(id);
  if (!m) return null;
  const tier = CONFIG.paidBoosts.tiers.find((x) => x.hours === Number(m[2]));
  return tier ? { kind: m[1] as PaidBoostKind, hours: tier.hours, price: tier.price } : null;
}

/** Offer id for the all-three bundle of one length, e.g. 'boost-all-24'. */
export const bundleOfferId = (hours: number) => `boost-all-${hours}`;

/** The length and price a bundle offer id names; null when it is not one. */
export function parseBundleOffer(id: unknown): { hours: number; price: number } | null {
  if (typeof id !== 'string') return null;
  const m = /^boost-all-(\d+)$/.exec(id);
  const tier = m && CONFIG.paidBoosts.bundle.find((x) => x.hours === Number(m[1]));
  return tier ? { hours: tier.hours, price: tier.price } : null;
}

/** What the three kinds of one length cost bought one by one (the bundle's "was" price). */
export const bundleParts = (hours: number) => PAID_BOOST_KINDS.length * (CONFIG.paidBoosts.tiers.find((x) => x.hours === hours)?.price ?? 0);

/** The free clock with the same effect: the XP boost for both XP kinds, the Coin boost for Loot's coins. */
function freeTwin(b: Boosts, kind: PaidBoostKind): number {
  return kind === 'loot' ? b.coinUntil : b.xpUntil;
}

/** When a paid kind ends (0 when never bought). */
export function paidUntil(b: Boosts | undefined, kind: PaidBoostKind): number {
  return b?.paid?.[kind] ?? 0;
}

/** Add `hours` of one paid kind: from where that kind ends, or from where a free one of the same effect ends, or now. */
export function extendPaid(b: Boosts, kind: PaidBoostKind, hours: number, now: number): number {
  const from = Math.max(now, paidUntil(b, kind), freeTwin(b, kind));
  const until = from + hours * 3_600_000;
  (b.paid ??= {})[kind] = until;
  return until;
}

/** XP (txp, cxp) or kill-coin (loot) multiplier at `now`: the paid or the free one of the same effect, never both. */
export function boostMult(b: Boosts, kind: PaidBoostKind, now: number): number {
  const paid = paidUntil(b, kind) > now ? M[kind] : 1;
  const free = freeTwin(b, kind) > now ? (kind === 'loot' ? CONFIG.coinBoostMult : CONFIG.xpBoostMult) : 1;
  return Math.max(paid, free);
}

